import { AlertProps, Snackbar, SnackbarProps } from "@mui/material";
import { ReactNode, useCallback, useMemo } from "react";
import { useEventCallback } from "usehooks-ts";

import { Alert } from "../../Alert/Alert";

const SNACKBAR_TOP_OFFSET = 20;
const SNACKBAR_HEIGHT = 60;

const anchorOrigin = {
  vertical: "top",
  horizontal: "center",
} as const;

export type SnackbarSeverity = AlertProps["severity"];

interface MessageProps {
  message: ReactNode;
  severity: SnackbarSeverity;
  index: number;
  autoHideDuration?: number;
  messageId?: string;
  onClose: () => void;
}

// TODO - refactor: Review this component to use Alert's action prop and onClose to have the X button
export const Message = (props: MessageProps) => {
  const { message, severity, autoHideDuration, messageId, onClose, index } =
    props;

  const handleClose = useCallback<NonNullable<SnackbarProps["onClose"]>>(
    (event, reason) => {
      if (reason === "clickaway") return;
      onClose();
    },
    [onClose],
  );

  const handleExited = useEventCallback(onClose);
  const transitionProps = useMemo(
    () => ({
      onExited: handleExited,
    }),
    [handleExited],
  );

  const snackbarSx = useMemo(
    () => ({
      top: `${SNACKBAR_TOP_OFFSET + index * SNACKBAR_HEIGHT}px !important`, // !important required as MUI for a position when `anchorOrigin` is set.
    }),
    [index],
  );

  const alertSx = useMemo(
    () => ({
      alignItems: "center", // To make sure the icon is always aligned with the text, especially when the message is complex
      p: 1,
      backgroundColor: severity === "info" ? "model.dark" : `${severity}.dark`,
      color: "secondary.1",
      "& .MuiAlert-icon": {
        color: "secondary.1",
      },
    }),
    [severity],
  );

  return (
    <Snackbar
      open
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
      sx={snackbarSx}
      TransitionProps={transitionProps}
    >
      <Alert
        severity={severity}
        data-testid="snackbar"
        data-messageid={messageId}
        sx={alertSx}
        onClose={!autoHideDuration ? onClose : undefined}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};
